import { useState } from 'react';
import { Search, Star, MessageSquare, FileText, Globe, Sparkles, LayoutDashboard, Users, Kanban, PhoneCall, FileBarChart, Bell } from 'lucide-react';
import { tabStudentFeatures, tabConsultancyFeatures } from '@/mocks/homeData';

const studentIconMap: Record<string, React.ReactNode> = {
  Search: <Search className="w-5 h-5 text-gold-500" />,
  Star: <Star className="w-5 h-5 text-gold-500" />,
  MessageSquare: <MessageSquare className="w-5 h-5 text-gold-500" />,
  FileText: <FileText className="w-5 h-5 text-gold-500" />,
  Globe: <Globe className="w-5 h-5 text-gold-500" />,
  Sparkles: <Sparkles className="w-5 h-5 text-gold-500" />,
};

const consultancyIconMap: Record<string, React.ReactNode> = {
  LayoutDashboard: <LayoutDashboard className="w-5 h-5 text-navy-500" />,
  Users: <Users className="w-5 h-5 text-navy-500" />,
  Kanban: <Kanban className="w-5 h-5 text-navy-500" />,
  PhoneCall: <PhoneCall className="w-5 h-5 text-navy-500" />,
  FileBarChart: <FileBarChart className="w-5 h-5 text-navy-500" />,
  Bell: <Bell className="w-5 h-5 text-navy-500" />,
};

export default function FeaturesSection() {
  const [activeTab, setActiveTab] = useState<'students' | 'consultancies'>('students');

  const features = activeTab === 'students' ? tabStudentFeatures : tabConsultancyFeatures;
  const iconMap = activeTab === 'students' ? studentIconMap : consultancyIconMap;

  return (
    <section id="features" className="bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <span className="text-xs font-semibold tracking-widest text-gold-600 uppercase">Features</span>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-navy-600">
              Everything You Need, Nothing You Don't
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-10 md:mb-14">
            <div className="inline-flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setActiveTab('students')}
                className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'students'
                    ? 'bg-gold-500 text-navy-700 shadow-sm'
                    : 'text-gray-500 hover:text-navy-600'
                }`}
              >
                For Students
              </button>
              <button
                onClick={() => setActiveTab('consultancies')}
                className={`px-6 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'consultancies'
                    ? 'bg-navy-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-navy-600'
                }`}
              >
                For Consultancies
              </button>
            </div>
          </div>

          {/* Feature Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-gray-50 rounded-xl p-6 md:p-8 hover:shadow-md transition-all group"
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  activeTab === 'students' ? 'bg-gold-50' : 'bg-navy-50'
                }`}>
                  {iconMap[feature.icon]}
                </div>
                <h3 className="text-lg font-semibold text-navy-600">{feature.title}</h3>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}